// MetricsDiffDashboard.js  v2
// -----------------------------------------------------------------------------
// Compare two metrics.log files (Run A vs Run B)
// Adds: time-series latency plot + correlation summary
// -----------------------------------------------------------------------------
import React, { useState } from "react";
import * as d3 from "d3";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  BarChart,
  Bar,
} from "recharts";
import "./MetricsDashboard.css";
import Papa from "papaparse";

function summarizeMetrics(entries) {
  const meanLatency = d3.mean(entries.map(e => e.latency?.mean ?? 0));
  const sdLatency = d3.mean(entries.map(e => e.latency?.sd ?? 0));
  const phonAcc = d3.mean(entries.map(e => e.phonAcc ?? 0));
  const visAcc = d3.mean(entries.map(e => e.visAcc ?? 0));
  const entropy = d3.mean(entries.map(e => e.entropy ?? 0));
  const mismatch = d3.mean(entries.map(e => e.mismatch ?? 0));
  return { meanLatency, sdLatency, phonAcc, visAcc, entropy, mismatch };
}

function computeDiff(a, b) {
  const diff = {};
  for (const k of Object.keys(a)) diff[k] = (b[k] ?? 0) - (a[k] ?? 0);
  return diff;
}

export default function MetricsDiffDashboard() {
  const [runA, setRunA] = useState(null);
  const [runB, setRunB] = useState(null);
  const [summaryA, setSummaryA] = useState(null);
  const [summaryB, setSummaryB] = useState(null);
  const [diff, setDiff] = useState(null);
  const [correlation, setCorrelation] = useState(null);
  const [csvA, setCsvA] = useState(null);
  const [csvB, setCsvB] = useState(null);
  const [csvAccuracy, setCsvAccuracy] = useState(null);
  // Parse metrics.log → array
  const parseFile = async file => {
    const text = await file.text();
    return text
      .split("\n")
      .map(l => l.trim())
      .filter(l => l.startsWith("{"))
      .map(l => {
        try {
          const j = JSON.parse(l);
          // use timestamp index for time series
          j._time = new Date(j.ts || Date.now()).getTime();
          return j;
        } catch {
          return {};
        }
      });
  };

  const handleFile = async (e, which) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const data = await parseFile(file);
    const summary = summarizeMetrics(data);
    if (which === "A") {
      setRunA(data);
      setSummaryA(summary);
    } else {
      setRunB(data);
      setSummaryB(summary);
    }
  };

  const handleCompare = () => {
    if (!summaryA || !summaryB) return;
    setDiff(computeDiff(summaryA, summaryB));

    // compute correlation of latency.mean between runs
    const len = Math.min(runA.length, runB.length);
    const arrA = runA.slice(0, len).map(e => e.latency?.mean ?? 0);
    const arrB = runB.slice(0, len).map(e => e.latency?.mean ?? 0);
    const meanA = d3.mean(arrA);
    const meanB = d3.mean(arrB);
    const num = d3.sum(arrA.map((v, i) => (v - meanA) * (arrB[i] - meanB)));
    const denom =
      Math.sqrt(d3.sum(arrA.map(v => (v - meanA) ** 2))) *
      Math.sqrt(d3.sum(arrB.map(v => (v - meanB) ** 2)));
    setCorrelation(num / denom);
  };

  // Δ summary bar data
  const chartData = diff
    ? [
        { metric: "Latency (s)", diff: diff.meanLatency },
        { metric: "Phoneme Accuracy", diff: diff.phonAcc },
        { metric: "Viseme Accuracy", diff: diff.visAcc },
        { metric: "Entropy (bits)", diff: diff.entropy },
        { metric: "Mismatch Rate", diff: diff.mismatch },
      ]
    : [];

  // time-series line data
  const timeSeries =
    runA && runB
      ? runA.map((a, i) => ({
          t: i,
          latencyA: a.latency?.mean ?? 0,
          latencyB: runB[i]?.latency?.mean ?? null,
        }))
      : [];

  const parseCSV = async (file) => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: false,
        dynamicTyping: true,
        skipEmptyLines: true,
        complete: (results) => {
          try {
            // Drop first row and first column if labels exist
            let rows = results.data;
            if (typeof rows[0][0] === "string" && rows[0][0].match(/Prev|Phoneme/i)) {
              rows = rows.slice(1); // remove header row
            }
            const matrix = rows.map(r => r.slice(1).map(Number)); // remove left label col
            resolve(matrix);
          } catch (err) {
            reject(err);
          }
        },
        error: (err) => reject(err),
      });
    });
  };

  function correlation2D(a, b) {
    const flatA = a.flat();
    const flatB = b.flat();
    const meanA = d3.mean(flatA);
    const meanB = d3.mean(flatB);
    const num = d3.sum(flatA.map((v, i) => (v - meanA) * (flatB[i] - meanB)));
    const den = Math.sqrt(
      d3.sum(flatA.map(v => (v - meanA) ** 2)) *
      d3.sum(flatB.map(v => (v - meanB) ** 2))
    );
    return den === 0 ? 0 : num / den;
  }

  const handleCsv = async (e, which) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const data = await parseCSV(file);
    if (which === "A") setCsvA(data);
    else setCsvB(data);
  };

  return (
    <div
      style={{
        background: "#111",
        color: "#eee",
        padding: "1rem",
        borderRadius: "1rem",
        boxShadow: "0 0 20px rgba(0,0,0,0.5)",
        maxWidth: 900,
        margin: "2rem auto",
        textAlign: "center",
      }}
    >
      <h2 style={{ color: "#00ffcc" }}>Metrics Difference Dashboard v2</h2>

      <div style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center", 
            gap: "1rem",
            marginTop: "1rem",
            }}>
        <label style={{ cursor: "pointer" }}>
          <input
            type="file"
            accept=".log,.jsonl,.txt"
            style={{ display: "none" }}
            onChange={e => handleFile(e, "A")}
          />
          <span className="action-btn">Load Run A</span>
        </label>
        <label style={{ cursor: "pointer" }}>
          <input
            type="file"
            accept=".log,.jsonl,.txt"
            style={{ display: "none" }}
            onChange={e => handleFile(e, "B")}
          />
          <span className="action-btn" style={{
            cursor: "pointer",
          }}>Load Run B</span>
        </label>
        <button
          className="action-btn"
          onClick={handleCompare}
          disabled={!summaryA || !summaryB}
          style={{
            background: "#00ffcc",
            color: "#111",
            border: "none",
            padding: "0.5rem 1rem",
            borderRadius: "8px",
            cursor: "pointer",
          }}
        >
          Compare
        </button>
      </div>

      {/* ─── Δ-BAR CHART ─────────────────────────────────────────────── */}
      {diff && (
        <>
          <h3 style={{ marginTop: "2rem", color: "#fff" }}>Δ (Run B – Run A)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="metric" tick={{ fill: "#ccc" }} />
              <YAxis tick={{ fill: "#ccc" }} />
              <Tooltip
                contentStyle={{
                  background: "#222",
                  border: "1px solid #00ffcc",
                  color: "#fff",
                }}
              />
              <Bar dataKey="diff" fill="#00ffcc" animationDuration={800} />
            </BarChart>
          </ResponsiveContainer>
        </>
      )}

      {/* ─── LATENCY TIME SERIES ─────────────────────────────────────── */}
      {timeSeries.length > 0 && (
        <>
          <h3 style={{ marginTop: "2rem", color: "#fff" }}>
            Latency over Time (s)
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={timeSeries}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="t" tick={{ fill: "#ccc" }} />
              <YAxis tick={{ fill: "#ccc" }} />
              <Tooltip
                contentStyle={{
                  background: "#222",
                  border: "1px solid #00ffcc",
                  color: "#fff",
                }}
              />
              <Legend wrapperStyle={{ color: "#ccc" }} />
              <Line
                type="monotone"
                dataKey="latencyA"
                stroke="#00aaff"
                dot={false}
                name="Run A"
              />
              <Line
                type="monotone"
                dataKey="latencyB"
                stroke="#ff77ff"
                dot={false}
                name="Run B"
              />
            </LineChart>
          </ResponsiveContainer>
        </>
      )}

      {/* ─── CORRELATION SUMMARY ─────────────────────────────────────── */}
      {correlation !== null && (
        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.1rem",
            color: correlation > 0.85 ? "#00ffcc" : "#ff7777",
          }}
        >
          Latency correlation r = {correlation.toFixed(3)}
        </div>
      )}
      
      {/* ─── CSV COMPARISON ─────────────────────────────────────────────── */}
      <div
        style={{
          marginTop: "2rem",
          padding: "1rem",
          borderTop: "1px solid #333",
          textAlign: "center",
        }}
      >
        <h3 style={{ color: "#00ffcc" }}>Transition Grid Accuracy (CSV Compare)</h3>
        <div style={{ display: "flex", justifyContent: "center", gap: "1rem", marginBottom: "1rem" }}>
          <label style={{ cursor: "pointer" }}>
            <input
              type="file"
              accept=".csv"
              style={{ display: "none" }}
              onChange={(e) => handleCsv(e, "A")}
            />
            <span className="action-btn">Load CSV A</span>
          </label>
          <label style={{ cursor: "pointer" }}>
            <input
              type="file"
              accept=".csv"
              style={{ display: "none" }}
              onChange={(e) => handleCsv(e, "B")}
            />
            <span className="action-btn">Load CSV B</span>
          </label>
          <button
            className="action-btn"
            onClick={() => {
              if (csvA && csvB) {
                const corr = correlation2D(csvA, csvB);
                setCsvAccuracy(corr);
              } else {
                alert("Please load both CSV files first.");
              }
            }}
            style={{
              background: "#00ffcc",
              color: "#111",
              border: "none",
              padding: "0.5rem 1rem",
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            Compare CSVs
          </button>
        </div>

        {csvAccuracy !== null && (
          <div style={{
            marginTop: "0.5rem",
            fontSize: "1.1rem",
            color: csvAccuracy > 0.85 ? "#00ffcc" : "#ff7777",
          }}>
            Accuracy r = {csvAccuracy.toFixed(3)}
          </div>
        )}
      </div>
    </div>
  );
}
